// GRUNNBILDELAGET — åpne kilder på kartet, med ærlig usikkerhet og flyttbar markør.
//
// ── ⚠ EIERBESLUTNING 2026-08-18 STYRER HELE FILA ────────────────────────────
// Jeg foreslo at en hendelse uten ærlig punkt skulle stå UTEN posisjon — bare i
// tidslinja. Eier overstyrte:
//
//   «Jeg vil heller at politihendelsene legges i kart på "estimert posisjon", med
//   mulighet for å flytte den hvis den ikke er relevant — som med alle andre
//   hendelser, mål eller observasjoner. Hvis vi ikke er sikre — skal det vises, men
//   være ærlig på at vi faktisk ikke vet helt sikkert.»
//
// Beslutningen er bedre, og grunnen bør stå her: **en hendelse som ikke står på
// kartet kan ingen rette.** Muligheten til å dra markøren er nettopp det som gjør
// et estimat trygt — operatøren gjør sammenstillingen geokoderen ikke kunne. Å
// skjule hendelsen var å velge systemets renhet framfor operatørens
// situasjonsbilde.
//
// ── TRE TING SOM GJØR ESTIMATET ÆRLIG PÅ SKJERMEN ───────────────────────────
//
// 1. USIKKERHETSSIRKELEN ER EN EKTE SIRKEL I METER. `ringPolygon` fra contracts
//    (samme funksjon ildstøtten bruker til danger close) tegner den geodetisk, så
//    den krymper og vokser riktig med zoom. ⚠ Ikke `circle-radius`: den er i
//    PIKSLER, og en 30 km usikkerhet ville sett like stor ut som 300 m på et annet
//    zoomnivå. En usikkerhet som ikke skalerer er ikke en usikkerhet.
//
// 2. ESTIMERTE MARKØRER SER ANNERLEDES UT ENN STEDFESTEDE. Estimatet er en hul
//    ring med stiplet kant; et menneskesatt punkt er fylt. Forskjellen skal kunne
//    leses på en solskinnsskjerm med nattfilter, altså i FORM og ikke bare i farge.
//
// 3. NÅR EN OPERATØR HAR RETTET, FORSVINNER SIRKELEN. `skalTegneUsikkerhet()` i
//    contracts sier nei for menneskesatte punkter, og det er riktig: en sirkel rundt
//    noe hun har pekt ut er meningsløs — hun pekte, hun anslo ikke.
import type { Map as MlMap, GeoJSONSource, MapMouseEvent } from 'maplibre-gl';
import {
  featureCollection, ringPolygon, skalTegneUsikkerhet, posisjonsTekst,
  erForeldet, foreldelsesTekst,
  type GeoFeature, type GrunnbildeObjekt,
} from '@skade/contracts';

/**
 * Terskel i piksler før en dragning regnes som en flytting.
 *
 * ⚠ SAMME TALL SOM `units.ts` (FLYTT_TERSKEL_PX = 8), og av samme grunn: uten den
 * gjorde en skjelvende hånd et klikk om til en endring av situasjonsbildet. Tallet
 * er duplisert framfor importert fordi `tools/units.ts` er et verktøy og dette er
 * et kartlag — men det er den SAMME regelen, og endres den ett sted skal den endres
 * begge.
 */
export const FLYTT_TERSKEL_PX = 8;

export interface GrunnbildelagHooks {
  /**
   * Operatøren har flyttet en markør. Kalles bare når terskelen er passert.
   *
   * ⚠ LAGET RETTER IKKE OBJEKTET SELV. Det ville gjort kartet til en kilde til
   * sannhet ved siden av hubben. Kalleren avgjør, kaller `flyttObjekt()` i
   * contracts, og sender den nye posisjonen videre — så rettingen får proveniens og
   * havner der alle ser den.
   */
  onFlyttet?: (id: string, lat: number, lon: number) => void;
  /** Klikk på en markør (uten dragning). */
  onKlikk?: (o: GrunnbildeObjekt) => void;
  /** Eier et annet verktøy klikket/dragningen nå? Da rører vi ingenting. */
  annenEier?: () => boolean;
}

export class Grunnbildelag {
  private objekter = new Map<string, GrunnbildeObjekt>();
  private dragging: string | null = null;
  private startPx: { x: number; y: number } | null = null;
  private flyttet = false;
  private klar = false;

  constructor(private map: MlMap, private hooks: GrunnbildelagHooks = {}) {
    if (map.isStyleLoaded()) this.bygg();
    else map.once('load', () => this.bygg());
  }

  private bygg(): void {
    if (this.klar) return;
    this.map.addSource('grunnbilde-usikkerhet', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('grunnbilde-omraade', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('grunnbilde', { type: 'geojson', data: featureCollection([]) });

    // ── USIKKERHETSSIRKELEN, NEDERST ────────────────────────────────────────
    // Gull: systemets ene «se hit / stol mindre»-aksent (designsystemet §1). Det er
    // presis den beskjeden en usikkerhetssirkel bærer. ⚠ Ikke `--danger`: en stor
    // rød flate over en bygd ville lest som et fiendtlig område, og «rød=hostil er
    // hellig».
    this.map.addLayer({
      id: 'grunnbilde-usikkerhet-fill',
      type: 'fill',
      source: 'grunnbilde-usikkerhet',
      paint: { 'fill-color': '#d4a03a', 'fill-opacity': 0.06 },
    });
    this.map.addLayer({
      id: 'grunnbilde-usikkerhet-line',
      type: 'line',
      source: 'grunnbilde-usikkerhet',
      paint: {
        'line-color': '#d4a03a',
        'line-width': 1,
        'line-opacity': 0.45,
        // Stiplet: kanten er ikke en grense i lendet, den er hvor lite vi vet.
        'line-dasharray': [3, 3],
      },
    });

    // ── KJENTE FLATER (MET-farevarsel, NVE-region) ──────────────────────────
    //
    // ⚠ EN KJENT FLATE ER IKKE EN USIKKERHETSSIRKEL, og de tegnes derfor ulikt. En
    // usikkerhetssirkel er stiplet og gull («vi vet ikke hvor») — en farevarsel-flate
    // er HELTRUKKEN, fordi kanten er en påstand MET faktisk står for.
    //
    // ⚠ Å TEGNE ET FAREVARSEL SOM EN PIN VILLE SAGT AT FAREN ER I MIDTEN. Det er den
    // ene feilen som kan sende noen inn i skredterrenget, og den grunnen er verdt å
    // gjenta her: flata er sannheten, punktet er bare et feste for etiketten.
    this.map.addLayer({
      id: 'grunnbilde-omraade-fill',
      type: 'fill',
      source: 'grunnbilde-omraade',
      paint: {
        // Farge etter MET-nivå. ⚠ IKKE `--aff-hostile`: et farevarsel er vær, ikke
        // en fiende. Rødt her er `--danger` (alarm), og «rød=hostil er hellig».
        'fill-color': ['get', 'farge'],
        'fill-opacity': ['case', ['get', 'foreldet'], 0.05, 0.12],
      },
    });
    this.map.addLayer({
      id: 'grunnbilde-omraade-line',
      type: 'line',
      source: 'grunnbilde-omraade',
      paint: {
        'line-color': ['get', 'farge'],
        'line-width': 1.5,
        'line-opacity': ['case', ['get', 'foreldet'], 0.3, 0.7],
      },
    });

    // ── MARKØREN ────────────────────────────────────────────────────────────
    // ⚠ FORMFORSKJELL, IKKE BARE FARGEFORSKJELL. Estimat = hul ring (tykk kant,
    // gjennomsiktig midte). Stedfestet/menneskesatt = fylt. Det skal kunne leses i
    // solskinn med nattfilter, der fargeforskjeller forsvinner først.
    this.map.addLayer({
      id: 'grunnbilde',
      type: 'circle',
      source: 'grunnbilde',
      paint: {
        'circle-radius': 7,
        'circle-color': ['case', ['get', 'estimert'], 'rgba(0,0,0,0)', ['get', 'farge']],
        // FORELDET (eierbeslutning: grå etter et døgn). ⚠ DEMPET, IKKE SKJULT:
        // markøren beholder plassen sin, formen sin og klikket sitt. En foreldet
        // hendelse er fortsatt en hendelse — det er ferskheten vi ikke står for.
        'circle-opacity': ['case', ['get', 'foreldet'], 0.35, 0.9],
        'circle-stroke-color': ['get', 'farge'],
        'circle-stroke-opacity': ['case', ['get', 'foreldet'], 0.45, 1],
        'circle-stroke-width': ['case', ['get', 'estimert'], 2.5, 1.5],
      },
    });
    this.map.addLayer({
      id: 'grunnbilde-label',
      type: 'symbol',
      source: 'grunnbilde',
      layout: {
        // ⚠ ETIKETTEN BÆRER «~» FOR ESTIMAT. En tekst som sier «Trafikk» ved siden
        // av en hul ring krever at operatøren har lært ringformen; tildeteksten
        // krever ingenting.
        'text-field': ['get', 'etikett'],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-offset': [0, 1.3],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': ['case', ['get', 'foreldet'], '#5b6b5b', '#e6eee6'],
        'text-halo-color': '#0a0c0a',
        'text-halo-width': 1.5,
      },
    });

    this.map.on('mousedown', 'grunnbilde', this.onDown);
    this.map.on('click', 'grunnbilde', this.onClick);
    this.map.on('mouseenter', 'grunnbilde', () => {
      if (!this.dragging && !this.hooks.annenEier?.()) this.map.getCanvas().style.cursor = 'move';
    });
    this.map.on('mouseleave', 'grunnbilde', () => {
      if (!this.dragging && !this.hooks.annenEier?.()) this.map.getCanvas().style.cursor = '';
    });
    this.klar = true;
  }

  /** Erstatt hele settet. Objekter uten posisjon hoppes over — de bor i tidslinja. */
  render(objekter: readonly GrunnbildeObjekt[]): void {
    this.objekter = new Map(objekter.map((o) => [o.id, o]));
    this.tegn();
  }

  /** Ett objekt oppdatert (typisk etter en retting). */
  oppdater(o: GrunnbildeObjekt): void {
    this.objekter.set(o.id, o);
    this.tegn();
  }

  private tegn(): void {
    if (!this.klar) return;
    const naa = Date.now();
    const punkter: GeoFeature[] = [];
    const sirkler: GeoFeature[] = [];
    const flater: GeoFeature[] = [];
    for (const o of this.objekter.values()) {
      if (o.lat === undefined || o.lon === undefined) continue;
      const estimert = !!o.usikkerhetM && skalTegneUsikkerhet(o);
      const foreldet = erForeldet(o, naa);
      punkter.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [o.lon, o.lat] },
        properties: {
          id: o.id,
          estimert,
          foreldet,
          farge: fargeFor(o),
          // Tilde-prefiks når posisjonen er et estimat — se kommentaren i laget.
          etikett: `${estimert ? '~' : ''}${o.type}`,
          // Tittelen er ferdig formulert i contracts, så kart og detaljpanel ikke
          // kan si to ulike ting om samme posisjon (§3).
          // ⚠ FORELDELSESTEKSTEN STÅR I TITTELEN, ikke bare i fargen. «Ikke
          // oppdatert av kilden på 31 t — saken kan fortsatt være åpen» er
          // opplysningen; grått alene kan leses som «håndtert».
          tittel: [o.tittel, posisjonsTekst(o, naa), foreldelsesTekst(o, naa)]
            .filter(Boolean).join('\n'),
        },
      });
      // ⚠ FLATA TEGNES FØRST OG UAVHENGIG AV `estimert`. En kjent flate har ingen
      // usikkerhet, så den ville aldri kommet inn i `estimert`-grenen — og da hadde
      // farevarselet stått som en naken markør.
      if (o.omraade?.length) {
        flater.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: o.omraade.map((r) => r.map((p) => [p.lon, p.lat])) },
          properties: { id: o.id, foreldet, farge: flatefargeFor(o) },
        });
      }
      if (estimert) {
        sirkler.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ringPolygon({ lat: o.lat, lon: o.lon }, o.usikkerhetM!)] },
          properties: { id: o.id },
        });
      }
    }
    (this.map.getSource('grunnbilde') as GeoJSONSource | undefined)
      ?.setData(featureCollection(punkter) as never);
    (this.map.getSource('grunnbilde-usikkerhet') as GeoJSONSource | undefined)
      ?.setData(featureCollection(sirkler) as never);
    (this.map.getSource('grunnbilde-omraade') as GeoJSONSource | undefined)
      ?.setData(featureCollection(flater) as never);
  }

  private onClick = (e: MapMouseEvent & { features?: Array<{ properties?: Record<string, unknown> }> }) => {
    if (this.flyttet || this.hooks.annenEier?.()) return;
    const id = String(e.features?.[0]?.properties?.id ?? '');
    const o = this.objekter.get(id);
    if (o) this.hooks.onKlikk?.(o);
  };

  private onDown = (e: MapMouseEvent & { features?: Array<{ properties?: Record<string, unknown> }> }) => {
    if (this.hooks.annenEier?.()) return;
    const id = String(e.features?.[0]?.properties?.id ?? '');
    const o = this.objekter.get(id);
    if (!o) return;
    // ⚠ EN KJENT FLATE KAN IKKE DRAS. MET sitt polygon er ikke noe en operatør kan
    // flytte ved å ta tak i etiketten — og en «rettet» posisjon på et farevarsel ville
    // vært en påstand om at faren har flyttet seg. Markøren er bare et feste.
    if (o.presisjon === 'omraade') return;
    e.preventDefault();
    this.dragging = id;
    this.flyttet = false;
    this.startPx = { x: e.point.x, y: e.point.y };
    this.map.dragPan.disable();
    this.map.on('mousemove', this.onMove);
    this.map.once('mouseup', this.onUp);
  };

  private onMove = (e: MapMouseEvent) => {
    if (!this.dragging) return;
    // Terskelen: små bevegelser skal ikke rikke markøren i det hele tatt.
    const s = this.startPx;
    if (s && Math.hypot(e.point.x - s.x, e.point.y - s.y) < FLYTT_TERSKEL_PX) return;
    this.flyttet = true;
    const o = this.objekter.get(this.dragging);
    if (!o) return;
    // Lokal forhåndsvisning så markøren følger musa. Den ekte rettingen skjer i
    // kalleren, og et nytt `render()` derfra er sannheten.
    this.objekter.set(this.dragging, { ...o, lat: e.lngLat.lat, lon: e.lngLat.lng });
    this.tegn();
  };

  private onUp = () => {
    const id = this.dragging;
    this.dragging = null;
    this.map.off('mousemove', this.onMove);
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = '';
    if (!id || !this.flyttet) return;
    const o = this.objekter.get(id);
    if (o?.lat !== undefined && o?.lon !== undefined) this.hooks.onFlyttet?.(id, o.lat, o.lon);
    // `flyttet` nullstilles på neste `mousedown`, ikke her: `click` fyrer ETTER
    // `mouseup`, og uten dette ville en dragning også åpnet detaljpanelet.
  };

  /** Skjul laget helt (av/på-bryter i sidebaren). */
  synlig(paa: boolean): void {
    if (!this.klar) return;
    for (const id of [
      'grunnbilde', 'grunnbilde-label',
      'grunnbilde-usikkerhet-fill', 'grunnbilde-usikkerhet-line',
      'grunnbilde-omraade-fill', 'grunnbilde-omraade-line',
    ]) {
      if (this.map.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', paa ? 'visible' : 'none');
    }
  }
}

/**
 * Farge per objekt.
 *
 * ⚠ IKKE TILHØRIGHETSFARGER. Et grunnbildeobjekt er en HENDELSE fra en åpen kilde,
 * ikke en aktør med militær tilhørighet — og «rød=hostil er hellig» (designsystemet
 * §1). Vi bruker derfor status-/kategorifargene: gull for det som haster, blått for
 * det øvrige, sage for det som er rettet av et menneske.
 */
function fargeFor(o: GrunnbildeObjekt): string {
  if (o.posisjonKilde === 'operator') return '#8ba888'; // --sage: et menneske står bak
  // En kjent flate er ikke et estimat, og markøren skal ikke bruke «stol mindre»-gull.
  if (o.presisjon === 'omraade') return flatefargeFor(o);
  if (o.presisjon === 'punkt' || o.presisjon === 'naeromraade') return '#5b9bd5'; // --info
  return '#d4a03a'; // --gold: estimert, «se hit / stol mindre»
}

/**
 * Farge på en kjent flate, etter kildens eget alvorlighetsnivå.
 *
 * ⚠ LESER MET-NIVÅET UT AV `forbehold`-TEKSTEN, som `varselForbehold()` skriver
 * («MET-nivå 2 (yellow, Moderate)»). Det er stygt, og alternativet var styggere: å
 * legge et `alvor`-felt på GrunnbildeObjekt som bare én kilde fyller, og som de andre
 * adapterne måtte huske å ikke sette. Nivået hører til varselet, ikke til
 * objektmodellen — og den dagen en annen kilde trenger samme skala, er det tid for
 * feltet.
 *
 * ⚠ IKKE `--aff-hostile`. Et farevarsel er vær, ikke en fiende; rødt her er
 * `--danger` (alarm), og «rød=hostil er hellig».
 */
function flatefargeFor(o: GrunnbildeObjekt): string {
  const f = o.forbehold;
  if (f.includes('MET-nivå 3')) return '#c23a3a'; // --danger
  if (f.includes('MET-nivå 2')) return '#d4a03a'; // --gold
  if (f.includes('MET-nivå 1')) return '#5b9bd5'; // --info
  // Ukjent nivå: gull, som alt annet vi ikke står helt for.
  return '#d4a03a';
}
